import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import CheckboxField from '../../../components/form/CheckboxField'
import Button from '../../../components/ui/button'
import Index from '../../../components/ui/popup'
import RangeInput from '../../../components/ui/rangeinput'

import { Form, Field, FieldProps, FormikProps, FormikValues, useFormikContext } from 'formik'
import { RootState } from '../../../store/rootReducer'
import { GeoState } from '../../../store/geo/geoTypings'

import { ages, genders } from '../../../constants'
import TextArea from '../../../components/ui/textArea'
import { updateGeoOptions } from '../../../api/geo'
import { updateMessage } from '../../../store/geo/geoSlice'
import SelectField from '../../../components/form/SelectField'
import './geoFilter.sass'
import { openMenu } from '../../../store/common/commonSlice'

interface Props {
    showMoreFilters: boolean
    setShowMoreFilters: (state: boolean) => void
}

const GeoFilter: React.FunctionComponent<Props> = ({ showMoreFilters, setShowMoreFilters }: Props) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const formik = useFormikContext()

    const [windowWidth, setWindowWidth] = useState(
        typeof document !== 'undefined' ? document.documentElement.clientWidth : 0
    )
    const [humorDialog, setHumorDialog] = useState(false)

    const handleCheckbox = (form: FormikProps<FormikValues>) => {
        !showMoreFilters && form.handleSubmit()
    }

    const { filters, message, hidePosition } = useSelector<RootState, GeoState>((state) => state.geo)
    const { loggedIn } = useSelector((state: RootState) => state.member)

    useEffect(() => {
        if (document)
            window.addEventListener('resize', () => {
                setWindowWidth(document.documentElement.clientWidth)
            })
    }, [])

    useEffect(() => {
        const localFilters = JSON.parse(localStorage.getItem('geoFilters'))
        if (localFilters) {
            formik.setFieldValue('humor', message)
            formik.setFieldValue('gender', localFilters.gender)
            formik.setFieldValue('age', localFilters.age)
            formik.setFieldValue('followers', localFilters.followers)
            formik.setFieldValue('followees', localFilters.followees)
        }
    }, [])

    useEffect(() => {
        if (filters) formik.setFieldValue('shareLocation', filters.shareMyLocation)
    }, [filters])

    return (
        <div className="filter">
            <Form>
                {!hidePosition && (
                    <div className={'myHumor'}>
                        {t('My mood')} {message}
                        <img
                            onClick={() => {
                                if (loggedIn) {
                                    setHumorDialog(!humorDialog)
                                } else {
                                    dispatch(openMenu())
                                }
                            }}
                            src={require('./../../../assets/ic-edit.svg')}
                        />
                    </div>
                )}
                {humorDialog && (
                    <div className={'humorDialog'} onClick={() => setHumorDialog(false)}>
                        <div className={'dialogWindow'} onClick={(e) => e.stopPropagation()}>
                            <p>{t('Change your humor')}</p>
                            <Field name="humor">
                                {({ field, form }: FieldProps) => (
                                    <>
                                        <TextArea
                                            form={form}
                                            field={field}
                                            placeholder={t('Humor')}
                                            emoji
                                            initValue={message}
                                        />
                                        <div className={'buttonsLine'}>
                                            <Button
                                                label={'Ok'}
                                                type={'submit'}
                                                onClick={() => {
                                                    updateGeoOptions({ message: form.values.humor })
                                                    dispatch(updateMessage(form.values.humor))
                                                    setHumorDialog(false)
                                                }}
                                            />
                                            <Button
                                                label={t('Cancel')}
                                                secondary
                                                onClick={() => setHumorDialog(false)}
                                            />
                                        </div>
                                    </>
                                )}
                            </Field>
                        </div>
                    </div>
                )}
                <div className="checkboxesgeo">
                    {filters && (
                        <Field name={'shareLocation'}>
                            {({ field, form }: FieldProps) => (
                                <div
                                    className={'hideLocationCheckbox'}
                                    onClick={() => {
                                        if (!loggedIn) dispatch(openMenu())
                                    }}
                                >
                                    <CheckboxField
                                        form={form}
                                        field={field}
                                        label={t('Share my position on the map')}
                                        onChangeCallback={handleCheckbox}
                                    />
                                    <img
                                        className={'markerInList'}
                                        src={require('./../../../assets/ic-map-marker.svg')}
                                    />
                                </div>
                            )}
                        </Field>
                    )}
                    {filters && (
                        <Field name="position">
                            {({ field, form }: FieldProps) => (
                                <RangeInput
                                    form={form}
                                    field={field}
                                    text={t('Move the cursor to display my position with more or less precision')}
                                    type={windowWidth <= 540 ? 'mobile' : ''}
                                    initialValue={
                                        filters && typeof filters.precision === 'number' ? filters.precision : 1000
                                    }
                                    maxRange={'3000'}
                                    notActive={!!filters.shareMyLocation}
                                />
                            )}
                        </Field>
                    )}
                    <div
                        className={'hiddenOptionsOnMobile'}
                        onClick={() => {
                            if (!loggedIn) dispatch(openMenu())
                        }}
                    >
                        <Field name="gender">
                            {({ field, form }: FieldProps) => (
                                <SelectField
                                    form={form}
                                    field={field}
                                    label={t('Gender')}
                                    options={genders}
                                    onChangeCallback={() => form.handleSubmit()}
                                />
                            )}
                        </Field>
                        <Field name="age">
                            {({ field, form }: FieldProps) => (
                                <SelectField
                                    form={form}
                                    field={field}
                                    label={t('Age')}
                                    options={ages}
                                    onChangeCallback={() => form.handleSubmit()}
                                />
                            )}
                        </Field>
                        {loggedIn && (
                            <Field name={'followers'}>
                                {({ field, form }: FieldProps) => (
                                    <CheckboxField
                                        form={form}
                                        field={field}
                                        label={t('My followers')}
                                        onChangeCallback={handleCheckbox}
                                    />
                                )}
                            </Field>
                        )}
                        {loggedIn && (
                            <Field name={'followees'}>
                                {({ field, form }: FieldProps) => (
                                    <CheckboxField
                                        noWrap
                                        form={form}
                                        field={field}
                                        label={t('My followees')}
                                        onChangeCallback={handleCheckbox}
                                    />
                                )}
                            </Field>
                        )}
                    </div>
                </div>
                {showMoreFilters && filters ? (
                    <Index onClose={() => setShowMoreFilters(false)}>
                        <div className="filterContent">
                            <Field name="gender">
                                {({ field, form }: FieldProps) => (
                                    <SelectField
                                        form={form}
                                        field={field}
                                        label={t('Gender')}
                                        options={genders}
                                        onChangeCallback={() => form.handleSubmit()}
                                    />
                                )}
                            </Field>
                            <Field name="age">
                                {({ field, form }: FieldProps) => (
                                    <SelectField
                                        form={form}
                                        field={field}
                                        label={t('Age')}
                                        options={ages}
                                        onChangeCallback={() => form.handleSubmit()}
                                    />
                                )}
                            </Field>
                            {loggedIn && (
                                <Field name={'followers'}>
                                    {({ field, form }: FieldProps) => (
                                        <CheckboxField
                                            form={form}
                                            field={field}
                                            label={t('My followers')}
                                            onChangeCallback={handleCheckbox}
                                        />
                                    )}
                                </Field>
                            )}
                            {loggedIn && (
                                <Field name={'followees'}>
                                    {({ field, form }: FieldProps) => (
                                        <CheckboxField
                                            form={form}
                                            field={field}
                                            label={t('My followees')}
                                            onChangeCallback={handleCheckbox}
                                        />
                                    )}
                                </Field>
                            )}
                            <Button id={'key_submit'} label={t('Search')} type="submit" />
                        </div>
                    </Index>
                ) : filters ? (
                    <div className="mobileButton">
                        <Button
                            label={t('Filters')}
                            type={'submit'}
                            onClick={() => {
                                if (loggedIn) {
                                    setShowMoreFilters(true)
                                } else {
                                    dispatch(openMenu())
                                }
                            }}
                        />
                    </div>
                ) : (
                    <></>
                )}
            </Form>
        </div>
    )
}

export default GeoFilter
